import { ArrowUpRight, FileCheck2, CalendarClock, Percent, Undo2, ShieldAlert } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { PageHero, CTABand } from "../components/PageParts";
import SectionHeading from "../components/SectionHeading";
import ProcessFlow from "../components/ProcessFlow";
import RiskNote from "../components/RiskNote";
import { FAQ, IllustrativeChartCard } from "../components/Cards";
import { FAQS, EXTERNAL_LINKS } from "../config/site";

const STEPS = [
  {
    title: "Participation",
    desc: "An investment amount is committed under the issuance's documented terms.",
  },
  {
    title: "Promissory token issued",
    desc: "A token evidencing the issuer's contractual promise — coupons and maturity conditions — is recorded against the participation.",
  },
  {
    title: "Coupons per schedule",
    desc: "Coupon payments follow the schedule defined in the product documentation, from the issuer to the participant.",
  },
  {
    title: "Maturity & redemption",
    desc: "At maturity, redemption follows the documented process — subject to issuer performance and solvency.",
  },
];

const TERMS = [
  {
    icon: CalendarClock,
    title: "Maturity & lock-in",
    text: "Each issuance defines its own maturity date and lock-in window. Confirmed terms are provided in the offering documentation; unconfirmed terms are labelled as such.",
  },
  {
    icon: Undo2,
    title: "Redemption",
    text: "Redemption follows the documented process at maturity, subject to the conditions and notice periods stated for the issuance.",
  },
  {
    icon: Percent,
    title: "Coupon terms",
    text: "Coupon rates and payment schedules are contractual terms of each issuance — published only when confirmed. Target yields are not guarantees.",
  },
  {
    icon: ShieldAlert,
    title: "Default risk",
    text: "Coupons and redemption depend on the issuer's ability to perform. Issuer default is possible and is disclosed, not hidden.",
  },
];

export default function VaultYield() {
  return (
    <>
      <Seo
        title="Vault Yield — Custodial, Independently Verified"
        path="/vault-yield"
        description="Yield-oriented vault products supported by documented performance verification and a clearly explained promissory token and coupon structure — with custody, counterparty and default risks disclosed."
      />

      <PageHero
        eyebrow="Offering 04 · Vault Yield"
        titleLines={["Verified yield,", "documented structure."]}
        intro="Yield-oriented vault products supported by documented performance verification and a clearly explained promissory token and coupon structure. Historical performance is shown only where independently verified; target yields are clearly distinguished and never presented as guarantees."
        badges={["Custodial", "Third-party audited & verified"]}
        image="https://images.unsplash.com/photo-1610375461369-d613b564f4c4?crop=entropy&cs=srgb&fm=jpg&q=85"
        imageAlt="Stacked gold bullion bars in close-up"
      />

      {/* Overview */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14">
            <SectionHeading
              eyebrow="The Structure"
              title={<>A promissory token, plainly explained</>}
              intro="Participation in a Vault Yield issuance is represented by a token that evidences a contractual promise by the issuer: coupon terms and maturity conditions set out in product documentation."
            />
            <Reveal delay={0.15} className="flex flex-col justify-center gap-5">
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
                This is not a deposit, and not equity in any fund. It is a contractual structure —
                which is why the issuer's identity, obligations and solvency risk are central to
                any participation decision.
              </p>
              <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
                Independent verification is what separates this product from marketing: reported
                results enter this website only when a third-party verifier has reviewed them
                against underlying records.
              </p>
              <div className="glass-gold rounded-2xl p-5 mt-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-400/80 mb-2">
                  Custody arrangement
                </p>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Custodial. Participating amounts are held under the issuance's custody
                  structure, and participants bear issuer counterparty and default risk.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="mt-20">
            <ProcessFlow idPrefix="yield" steps={STEPS} />
          </div>
        </div>
      </section>

      {/* Verification + methodology */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10 bg-ink-800/40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <SectionHeading
              eyebrow="Independent Verification"
              title={<>Audit claims only when documentation exists</>}
              intro="Verification reports are published here only when the supporting documentation is available. Until then, this placeholder stands — because a claim without a report is not transparency."
            />
            <Reveal delay={0.15}>
              <div className="glass-gold rounded-3xl p-7 flex items-start gap-4">
                <FileCheck2 size={20} className="text-gold-400 shrink-0 mt-1" />
                <div className="flex flex-col gap-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-400/80">
                    Verification status
                  </p>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    Verified performance reports will be published here when available, including
                    the verifier's identity, the reporting period, the realised figures and the
                    date the information was last updated.
                  </p>
                  <p className="text-sm text-zinc-500 leading-relaxed italic">
                    Status: awaiting a verification report cleared for publication.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="mt-16 grid lg:grid-cols-2 gap-8 items-start">
            <Reveal>
              <div className="glass rounded-3xl p-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-400/80 mb-5">
                  Yield calculation methodology
                </p>
                <ul className="flex flex-col gap-3.5 text-sm text-zinc-400 leading-relaxed">
                  <li className="flex gap-3">
                    <span className="text-gold-400 font-mono text-xs mt-0.5">01</span>
                    Historical performance: realised, independently verified results — labelled with period and source.
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold-400 font-mono text-xs mt-0.5">02</span>
                    Target / projected yields: forward-looking contractual objectives — clearly labelled, never guaranteed.
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold-400 font-mono text-xs mt-0.5">03</span>
                    The two are never combined, averaged or interpolated on this website.
                  </li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <IllustrativeChartCard testid="yield-illustrative-chart" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Terms */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="Contractual Terms"
            title={<>Maturity, lock-in, redemption, coupons</>}
            intro="These are the four pillars of every issuance's terms. Confirmed values are published per issuance in the offering documentation."
            align="center"
          />
          <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TERMS.map((t) => (
              <StaggerItem key={t.title} className="h-full">
                <div className="glass rounded-2xl p-6 h-full hover:border-gold-400/30 transition-colors duration-500">
                  <t.icon size={20} className="text-gold-400 mb-5" />
                  <h3 className="text-sm font-semibold text-ink-50 mb-2">{t.title}</h3>
                  <p className="text-xs text-zinc-500 leading-relaxed">{t.text}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Risk + FAQ */}
      <section className="py-24 lg:py-28 border-t border-gold-400/10">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <RiskNote
            lines={[
              "Custody risk: participating amounts are held under the issuance's custodial structure; participants bear the custodian's operational and counterparty risk.",
              "Counterparty and default risk: coupons and redemption depend on the issuer's ability and willingness to perform. Issuer default can result in partial or total loss.",
              "Liquidity risk: lock-in windows and maturity conditions can restrict exit. Terms are stated in each issuance's documentation.",
            ]}
          />
          <div className="mt-16">
            <SectionHeading eyebrow="FAQ" title={<>Frequently asked questions</>} />
            <div className="mt-10">
              <FAQ items={FAQS.yield} idPrefix="yield-faq" />
            </div>
          </div>
        </div>
      </section>

      <CTABand
        testid="yield-cta"
        title="Explore Vault Yield documentation."
        subtitle="The configured destination carries issuance documentation — or contact the team with any question first."
        primary={{ label: "Explore Vault Yield", to: EXTERNAL_LINKS.vaultYield, external: true }}
        secondary={{ label: "Contact Our Team", to: "/contact" }}
      />
    </>
  );
}